/**
 * Guided-section vocabulary for the confirm flow (SPEC-SOR-174 R1/R3/R5).
 *
 * The confirm flow's two steps (Upload → Confirm your filing) are subdivided
 * into four ordered CONFIRMATION units. Sections live WITHIN steps — the tab
 * order is unchanged and must not be re-derived from `SECTION_ORDER`; the two
 * orders answer different questions (which page the filer is on vs. which
 * approval must precede which).
 *
 * This module is deliberately dependency-free with respect to
 * `../types.js`: `EFilingWizardState` imports `GuidedSection` /
 * `SectionStatus` from here, so every predicate below takes a NARROW
 * structural slice of the wizard state instead of the full type. That keeps
 * the dependency one-directional (and the `.js` extensions here are the
 * Docker-build ESM rule, not an optional style).
 */
/**
 * Confirmation order (R1). A section may be confirmed only once every
 * strictly earlier section is confirmed.
 */
export const SECTION_ORDER = [
    'sec_court',
    'sec_case',
    'sec_parties',
    'sec_documents',
];
/**
 * A section's status, defaulting an absent entry to `pending`.
 *
 * The map is optional and sparse by design — a draft saved before the guided
 * model existed carries no map at all, and the classic flow never writes one
 * (R16), so `pending` has to be the reading of "nothing recorded" rather than
 * a value anyone stores.
 */
export function getSectionStatus(state, section) {
    return state.sectionStatuses?.[section] ?? 'pending';
}
/**
 * True when every section strictly EARLIER than `section` is confirmed
 * (vacuously true for `sec_court`, which has no predecessors).
 */
export function predecessorsConfirmed(state, section) {
    const index = SECTION_ORDER.indexOf(section);
    // An unknown section has no defined position in the ladder; treat it as
    // unsatisfied rather than vacuously ready.
    if (index < 0)
        return false;
    return SECTION_ORDER.slice(0, index).every((earlier) => getSectionStatus(state, earlier) === 'confirmed');
}
/**
 * R1 — the monotonicity invariant: whenever a section reads `confirmed`,
 * every strictly earlier section reads `confirmed` too.
 *
 * This is a CHECK on a state, not a repair: a state that violates it is a bug
 * upstream (a write that skipped the ladder), and callers should treat `false`
 * as "refuse", never as "fix up and continue".
 */
export function sectionOrderMonotone(state) {
    return SECTION_ORDER.every((section) => getSectionStatus(state, section) !== 'confirmed' ||
        predecessorsConfirmed(state, section));
}
/**
 * R5 — the court section is confirmable exactly when the filing state and
 * county are both set. `FilingCourt` is `orderInfo.state` + `orderInfo.county`
 * and nothing else: the county is the court key for case search, taxonomy and
 * document-type lists, so a half-set court is not a court.
 *
 * Biconditional, not a floor — no other condition may tighten or loosen it,
 * or the readiness the confirm control offers stops matching the readiness
 * R3 requires of a confirmed section.
 */
export function courtSectionConfirmable(state) {
    return Boolean(state.orderInfo.state) && Boolean(state.orderInfo.county);
}
